import { API_URL } from "~/lib/constants";

import { isBrowser } from "./is-browser";

declare global {
    interface Window {
        env: {
            SUPABASE_URL: string;
            SUPABASE_ANON_PUBLIC: string;
            SENTRY_DSN: string;
            VITE_APP_MIXPANEL_PROJECT_TOKEN: string;
            VITE_APP_DATADOG_CLIENT_TOKEN: string;
            ENV_NAME: string;
            SENTRY_ENABLED?: string;
            SUPPORT_EMAIL: string;
            PRODUCT_NAME: string;
            SERVER_URL: string;
        };
    }
}

declare global {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace NodeJS {
        interface ProcessEnv {
            SUPABASE_URL: string;
            SUPABASE_SERVICE_ROLE: string;
            SERVER_URL: string;
            SUPABASE_ANON_PUBLIC: string;
            SESSION_SECRET: string;
            LATITUDE_API_KEY: string;
            FIREFLIES_API_KEY: string;
            FIREFLIES_WEBHOOK_SECRET: string;
            SENTRY_DSN: string;
            SENTRY_ENABLED?: string;
            INNGEST_SERVICE_ID: string;
            INNGEST_SERVICE_NAME: string;
            INNGEST_BASE_URL: string;
            VITE_APP_MIXPANEL_PROJECT_TOKEN: string;
            MEETINGBAAS_API_KEY: string;
            NYLAS_WEBHOOK_SECRET: string;
            NYLAS_API_KEY: string;
            // Nifty secrets
            NIFTY_CLIENT_ID: string;
            NIFTY_CLIENT_SECRET: string;
            NIFTY_AUTHORIZE_URL: string;
            USE_MOCK_FIREFLIES?: string;
            CUSTOMERIO_API_KEY: string;
            DOCUMENT_ADMIN_BYPASS_TOKEN?: string;
            DATABASE_URL: string;
            ASSEMBLYAI_API_KEY?: string;
        }
    }
}

type EnvOptions = {
    isSecret?: boolean;
    isRequired?: boolean;
};
// Overloads so required envs are typed as string, optional as string | undefined
function getEnv(name: string): string;
function getEnv(
    name: string,
    options: { isSecret?: boolean; isRequired?: true },
): string;
function getEnv(
    name: string,
    options: { isSecret?: boolean; isRequired: false },
): string | undefined;
function getEnv(
    name: string,
    { isRequired, isSecret }: EnvOptions = { isSecret: true, isRequired: true },
): string | undefined {
    if (isBrowser && isSecret) return "";

    const source = (isBrowser ? window.env : process.env) ?? {};

    const value = source[name as keyof typeof source];

    if (!value && isRequired) {
        throw new Error(`${name} is not set`);
    }

    // AIDEV-NOTE: Trim whitespace to prevent header errors
    const trimmed = (value as string | undefined)?.trim();
    return isRequired ? (trimmed as string) : trimmed;
}

export const ENV_NAME = getEnv("ENV_NAME");

/**
 * Server env
 */
export const SERVER_URL = getEnv("SERVER_URL");
export const SUPABASE_SERVICE_ROLE = getEnv("SUPABASE_SERVICE_ROLE");
export const SESSION_SECRET = getEnv("SESSION_SECRET");
export const LATITUDE_API_KEY = getEnv("LATITUDE_API_KEY");
export const FIREFLIES_API_KEY = getEnv("FIREFLIES_API_KEY");
export const FIREFLIES_WEBHOOK_SECRET = getEnv("FIREFLIES_WEBHOOK_SECRET");
export const INNGEST_SERVICE_ID = getEnv("INNGEST_SERVICE_ID");
export const INNGEST_SERVICE_NAME = getEnv("INNGEST_SERVICE_NAME");
export const INNGEST_BASE_URL = getEnv("INNGEST_BASE_URL", {
    isRequired: false,
});
export const VITE_APP_MIXPANEL_PROJECT_TOKEN = getEnv(
    "VITE_APP_MIXPANEL_PROJECT_TOKEN",
);
export const ASSEMBLYAI_API_KEY = getEnv("ASSEMBLYAI_API_KEY", {
    isRequired: false,
});
export const MEETINGBAAS_API_KEY = getEnv("MEETINGBAAS_API_KEY");
export const NYLAS_WEBHOOK_SECRET = getEnv("NYLAS_WEBHOOK_SECRET", {
    isRequired: false,
});
export const NYLAS_API_KEY = getEnv("NYLAS_API_KEY", {
    isRequired: false,
});
export const MEETINGBAAS_WEBHOOK_URL = getEnv("MEETINGBAAS_WEBHOOK_URL", {
    isRequired: false,
});
export const RESEND_API_KEY = getEnv("RESEND_API_KEY");
export const ELEVENLABS_API_KEY = getEnv("ELEVENLABS_API_KEY");

export const DATADOG_API_KEY = getEnv("DATADOG_API_KEY", { isRequired: false });
export const DATADOG_CLIENT_TOKEN = getEnv("DATADOG_CLIENT_TOKEN", {
    isRequired: false,
});
export const OPENAI_API_KEY = getEnv("OPENAI_API_KEY");
export const ANTHROPIC_API_KEY = getEnv("ANTHROPIC_API_KEY");
export const GEMINI_API_KEY = getEnv("GEMINI_API_KEY", { isRequired: false });
export const CUSTOMERIO_API_KEY = getEnv("CUSTOMERIO_API_KEY");
export const DOCUSEAL_SIGNING_URL = getEnv("DOCUSEAL_SIGNING_URL", {
    isSecret: false,
    isRequired: false,
});
export const DOCUSEAL_API_KEY = getEnv("DOCUSEAL_API_KEY", {
    isRequired: false,
});
export const DATABASE_URL = getEnv("DATABASE_URL");
/**
 * Shared envs
 */
export const NODE_ENV = getEnv("NODE_ENV", {
    isSecret: false,
    isRequired: false,
});
export const SUPABASE_URL = getEnv("SUPABASE_URL", { isSecret: false });
export const SUPABASE_ANON_PUBLIC = getEnv("SUPABASE_ANON_PUBLIC", {
    isSecret: false,
});
export const SENTRY_DSN = getEnv("SENTRY_DSN");
export const SENTRY_ENABLED = getEnv("SENTRY_ENABLED", {
    isRequired: false,
    isSecret: false,
});
const vercelUrl = getEnv("VERCEL_URL", { isSecret: false });
export const SUPPORT_EMAIL =
    getEnv("SUPPORT_EMAIL", { isSecret: false, isRequired: false }) ||
    "hello@partscompanion.org";

export function getBrowserEnv() {
    return {
        ENV_NAME,
        SUPABASE_URL,
        SUPABASE_ANON_PUBLIC,
        DATADOG_CLIENT_TOKEN,
        SENTRY_DSN,
        SENTRY_ENABLED,
        VITE_APP_API_URL: API_URL,
        VERCEL_URL: vercelUrl,
        VITE_APP_VERCEL_URL: vercelUrl,
        VITE_APP_MIXPANEL_PROJECT_TOKEN,
        CUSTOMERIO_API_KEY,
        SUPPORT_EMAIL,
        PRODUCT_NAME:
            getEnv("PRODUCT_NAME", { isSecret: false, isRequired: false }) ||
            "IFS Companion",
        SERVER_URL,
    };
}

export const IS_FIREFLIES_POLLING_ENABLED = getEnv(
    "IS_FIREFLIES_POLLING_ENABLED",
    {
        isRequired: false,
    },
);

export const USE_MOCK_FIREFLIES = getEnv("USE_MOCK_FIREFLIES", {
    isRequired: false,
    isSecret: false,
});

// Nifty secrets
export const NIFTY_CLIENT_ID = getEnv("NIFTY_CLIENT_ID");
export const NIFTY_CLIENT_SECRET = getEnv("NIFTY_CLIENT_SECRET");
export const NIFTY_AUTHORIZE_URL = getEnv("NIFTY_AUTHORIZE_URL");

// AIDEV-NOTE: Admin bypass token for document operations
export const DOCUMENT_ADMIN_BYPASS_TOKEN = getEnv(
    "DOCUMENT_ADMIN_BYPASS_TOKEN",
    {
        isRequired: false,
    },
);
